import React, { FC, useCallback } from 'react';

import Link from '../../navigation/Link';
import InternalLink from '../../navigation/InternalLink';
import ExternalLink from '../../navigation/ExternalLink';
import ResourceLoader from '../../loader/ResourceLoader';
import Loader from '../../loader/Loader';

import { StyledFooterLink } from './FooterLinks.styles';
import getBaseUrl from '../../../utils/browser/getBaseUrl';

export interface MainFooterDynamicLinksProps {
  linksUrl: string;
  linksDomain: string;
  noLinksLoader?: boolean;
}

interface SiteCoreField { Name?: string; Value?: string }
interface SiteCoreResponse { value: { Fields: SiteCoreField[] }[] }
interface SiteCoreLink {
  text: string;
  url: string;
  target: '_self' | '_blank' | '_parent' | '_top';
  isInternal: boolean;
}

const MainFooterDynamicLinks: FC<MainFooterDynamicLinksProps> = ({ linksUrl, linksDomain, noLinksLoader = false }) => {
  const EmptyRender = useCallback(() => (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  ), []);

  const CustomLoader = useCallback(() => (
    <li><Loader /></li>
  ), []);

  const clearUrl = useCallback((field: SiteCoreField = {}) => {
    const { Name: type, Value: url } = field;

    if (type === 'Url') {
      return url;
    }

    if (type === 'Link' && url.startsWith('/') && linksDomain) {
      return `${linksDomain}${url}`;
    }

    if (type === 'Link') {
      if (url.toLowerCase().indexOf('/sitecore/') >= 0) {
        const linkHomePartIndex = url.toLowerCase().indexOf('home/');
        if (linkHomePartIndex >= 0) return `/cw/${url.toLowerCase().substr(linkHomePartIndex + 5)}`;
      }
    }
    return url;
  }, [linksDomain]);

  const testIsInternalLink = useCallback((linkUrl = '') => {
    const baseUrl = getBaseUrl();
    const fullLinkUrl = linkUrl.startsWith('http') ?
      linkUrl :
      `${document.location.origin}${linkUrl.startsWith('/') ? '' : '/'}${linkUrl}`;

    return fullLinkUrl.startsWith(baseUrl);
  }, []);

  const transformLinksResponse = useCallback((data: SiteCoreResponse): SiteCoreLink[] => (
    data?.value?.map(({ Fields }) => {
      const url = clearUrl(
        Fields.find(({ Name }) => Name === 'Url') || Fields.find(({ Name }) => Name === 'Link'),
      );
      const text = Fields.find(({ Name }) => Name === 'Title').Value;
      const target = Fields.find(({ Name }) => Name === 'Link Target')?.Value as '_self' | '_blank' | '_parent' | '_top';
      const isInternal = testIsInternalLink(url);

      return {
        url,
        text,
        target,
        isInternal,
      };
    }, [])
  ), [clearUrl, testIsInternalLink]);

  return (
    <ResourceLoader<SiteCoreLink[]>
      name="MainFooterDynamicLinksLinksResource"
      url={linksUrl}
      transformResponse={transformLinksResponse}
      CustomLoader={noLinksLoader ? EmptyRender : CustomLoader}
      CustomDataErrorMessage={noLinksLoader ? EmptyRender : null}
      autoRequestIfEmpty
      requestOnLanguageChange
    >
      {({ data: links }) => (
        links.map(({ text, url, target, isInternal }) => {
          const linkProps = target === '_blank' ? { target } : {};
          return (
            <StyledFooterLink key={text}>
              {isInternal && (
                <InternalLink
                  data-testid="FooterLinksLink"
                  href={url}
                  {...linkProps}
                >{text}
                </InternalLink>
              )}
              {!isInternal && target !== '_blank' && (
                <Link
                  data-testid="FooterLinksLink"
                  href={url}
                  target={target}
                >{text}
                </Link>
              )}
              {!isInternal && target === '_blank' && (
                <ExternalLink
                  data-testid="FooterLinksLink"
                  href={url}
                >{text}
                </ExternalLink>
              )}
            </StyledFooterLink>
          );
        })
      )}
    </ResourceLoader>
  );
};

export default MainFooterDynamicLinks;
